import { writable } from "svelte/store";

const apiBaseUrl = `${process.env.SVELTE_APP_REMOTE_URL}/api`;
export const HUBSPOT_AUTH_CHANGED_EVENT = "hubspot-auth-changed";
const defaultAuthDialogMessage = "Sign in to HubSpot to continue.";
const hubspotAuthErrorPatterns = [
  /no stored token/i,
  /authenticate first/i,
  /no refresh token/i,
  /authentication is required/i,
];

export interface HubspotAuthDialogState {
  open: boolean;
  message: string;
}

export interface FetchJsonWithHubspotAuthOptions {
  authRequiredMessage?: string;
  openDialogOnAuthError?: boolean;
  retrySession?: boolean;
}

export interface FetchJsonWithHubspotAuthResult<T = any> {
  response: Response;
  data: T | null;
  text: string;
}

export interface HubspotRequestResult<T = Record<string, any>> {
  success: boolean;
  message: string;
  authRequired?: boolean;
  data: T;
}

export interface HubspotOpportunitySyncResult extends Record<string, any> {
  success: boolean;
  opportunityId: number;
  message?: string;
  authRequired?: boolean;
}

export interface HubspotSyncOperationOptions {
  authRequiredMessage?: string;
  fallbackMessage?: string;
  openDialogOnAuthError?: boolean;
}

export const hubspotAuthDialog = writable<HubspotAuthDialogState>({
  open: false,
  message: defaultAuthDialogMessage,
});

export const defaultHubspotSyncAuthMessage =
  "Sign in to HubSpot to continue syncing.";
export const defaultHubspotOpportunityInitAuthMessage =
  "Sign in to HubSpot to finish setup.";

export class HubspotAuthRequiredError extends Error {
  constructor(message = defaultAuthDialogMessage) {
    super(message);
    this.name = "HubspotAuthRequiredError";
  }
}

export const closeHubspotAuthDialog = (): void => {
  hubspotAuthDialog.set({
    open: false,
    message: defaultAuthDialogMessage,
  });
};

export const openHubspotAuthDialog = (message = defaultAuthDialogMessage): void => {
  hubspotAuthDialog.set({
    open: true,
    message,
  });
};

export const isHubspotAuthRequiredError = (error: unknown): error is HubspotAuthRequiredError =>
  error instanceof HubspotAuthRequiredError;

const normalizeString = (value: unknown): string => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const parseJsonSafely = (text: string): any | null => {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const toObjectPayload = (value: unknown): Record<string, any> =>
  value && typeof value === "object" ? (value as Record<string, any>) : {};

export const extractApiMessage = (
  data: any,
  text = "",
  fallback = "Request failed."
): string => {
  const candidates = [
    data?.error?.message,
    data?.error?.details,
    data?.response?.error,
    data?.response?.message,
    data?.message,
    data?.Message,
    data?.error,
    text,
  ];

  for (const candidate of candidates) {
    const message = normalizeString(candidate);
    if (message) return message;
  }

  return fallback;
};

const isHubspotAuthMessage = (message: string): boolean =>
  hubspotAuthErrorPatterns.some((pattern) => pattern.test(message));

const buildHubspotAuthPrompt = (
  fallbackMessage: string | undefined,
  rawMessage: string
): string => {
  const prompt = normalizeString(fallbackMessage);
  if (prompt) return prompt;
  return normalizeString(rawMessage) || defaultAuthDialogMessage;
};

export const getHubspotAuthRequiredMessage = (
  response: Response | null,
  data: any,
  text = "",
  fallbackMessage?: string
): string | null => {
  const rawMessage = extractApiMessage(data, text, "");
  const hasAuthMessage = isHubspotAuthMessage(rawMessage);
  const hasRelevantStatus = response
    ? [401, 403, 500].includes(response.status) || !response.ok || hasAuthMessage
    : true;

  if (!hasRelevantStatus || !hasAuthMessage) return null;
  return buildHubspotAuthPrompt(fallbackMessage, rawMessage);
};

let refreshPromise: Promise<boolean> | null = null;

const dispatchHubspotAuthChanged = (): void => {
  window.dispatchEvent(new CustomEvent(HUBSPOT_AUTH_CHANGED_EVENT));
};

const storeRefreshToken = (refreshToken: string): void => {
  if (normalizeString(refreshToken)) {
    localStorage.setItem("hubspotRefreshToken", refreshToken);
  }
};

export const refreshHubspotSessionWithToken = async (
  refreshToken: string
): Promise<{ success: boolean; message: string }> => {
  const normalizedToken = normalizeString(refreshToken);
  if (!normalizedToken) {
    return { success: false, message: "Refresh token is required." };
  }

  try {
    const response = await fetch(`${apiBaseUrl}/hubspot-auth/refresh-token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ refreshToken: normalizedToken }),
    });
    const text = await response.text();
    const data = parseJsonSafely(text);

    if (!response.ok || !data?.accessToken) {
      return {
        success: false,
        message: extractApiMessage(data, text, "Invalid refresh token."),
      };
    }

    storeRefreshToken(normalizedToken);
    if (normalizeString(data?.refreshToken)) {
      storeRefreshToken(data.refreshToken);
    }

    dispatchHubspotAuthChanged();

    return { success: true, message: "Signed in successfully." };
  } catch (error: unknown) {
    return {
      success: false,
      message: error instanceof Error ? error.message : "Error validating refresh token.",
    };
  }
};

export const logoutHubspotSession = async (): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await fetch(`${apiBaseUrl}/hubspot-auth/delete-tokens`);
    const text = await response.text();
    const data = parseJsonSafely(text);

    if (!response.ok || data?.success === false) {
      return {
        success: false,
        message: extractApiMessage(data, text, "Failed to log out from HubSpot."),
      };
    }

    localStorage.removeItem("hubspotRefreshToken");
    dispatchHubspotAuthChanged();

    return { success: true, message: "Logged out successfully." };
  } catch (error: unknown) {
    return {
      success: false,
      message: error instanceof Error ? error.message : "Failed to log out from HubSpot.",
    };
  }
};

export const checkHubspotAuthenticationStatus = async (): Promise<boolean> => {
  const response = await fetch(`${apiBaseUrl}/hubspot-auth/status`);
  const data = await response.json().catch(() => ({}));
  return Boolean(response.ok && data?.authenticated);
};

export const restoreHubspotSession = async (): Promise<boolean> => {
  const refreshToken = normalizeString(localStorage.getItem("hubspotRefreshToken"));
  if (!refreshToken) return false;

  if (!refreshPromise) {
    refreshPromise = (async () => {
      const result = await refreshHubspotSessionWithToken(refreshToken);
      refreshPromise = null;
      return result.success;
    })();
  }

  return refreshPromise;
};

export const ensureHubspotAuthenticated = async (
  options: { tryRestore?: boolean } = {}
): Promise<boolean> => {
  const { tryRestore = true } = options;

  try {
    let authenticated = await checkHubspotAuthenticationStatus();
    if (!authenticated && tryRestore) {
      const restored = await restoreHubspotSession();
      if (restored) {
        authenticated = await checkHubspotAuthenticationStatus().catch(() => true);
      }
    }
    return authenticated;
  } catch {
    if (!tryRestore) return false;
    return restoreHubspotSession();
  }
};

export const beginHubspotOAuthLogin = async (): Promise<void> => {
  const appUrl = `${window.location.origin}${window.location.pathname}`;
  const response = await fetch(
    `${apiBaseUrl}/hubspot-auth/auth-url?appUrl=${encodeURIComponent(appUrl)}`
  );
  const text = await response.text();
  const data = parseJsonSafely(text);

  if (!response.ok || !normalizeString(data?.url)) {
    throw new Error(extractApiMessage(data, text, "Could not fetch HubSpot login URL."));
  }

  window.location.href = data.url;
};

export const completeHubspotOAuthCallback = async (
  code: string
): Promise<{ success: boolean; message: string }> => {
  const normalizedCode = normalizeString(code);
  if (!normalizedCode) {
    return { success: false, message: "Missing HubSpot OAuth code." };
  }

  try {
    const response = await fetch(
      `${apiBaseUrl}/hubspot-auth/callback?code=${encodeURIComponent(normalizedCode)}`
    );
    const text = await response.text();
    const data = parseJsonSafely(text);

    if (!response.ok || !data?.accessToken) {
      return {
        success: false,
        message: extractApiMessage(data, text, "HubSpot login failed."),
      };
    }

    if (normalizeString(data?.refreshToken)) {
      storeRefreshToken(data.refreshToken);
    }

    dispatchHubspotAuthChanged();

    return { success: true, message: "HubSpot login completed successfully." };
  } catch (error: unknown) {
    return {
      success: false,
      message: error instanceof Error ? error.message : "Error logging in with HubSpot.",
    };
  }
};

export const fetchJsonWithHubspotAuth = async <T = any>(
  input: RequestInfo | URL,
  init?: RequestInit,
  options: FetchJsonWithHubspotAuthOptions = {}
): Promise<FetchJsonWithHubspotAuthResult<T>> => {
  const attemptFetch = async (): Promise<FetchJsonWithHubspotAuthResult<T>> => {
    const response = await fetch(input, init);
    const text = await response.text();
    const data = parseJsonSafely(text);

    return {
      response,
      data,
      text,
    };
  };

  let result = await attemptFetch();
  let authMessage = getHubspotAuthRequiredMessage(
    result.response,
    result.data,
    result.text,
    options.authRequiredMessage
  );

  if (authMessage && options.retrySession !== false) {
    const restored = await restoreHubspotSession();
    if (restored) {
      result = await attemptFetch();
      authMessage = getHubspotAuthRequiredMessage(
        result.response,
        result.data,
        result.text,
        options.authRequiredMessage
      );
    }
  }

  if (authMessage) {
    if (options.openDialogOnAuthError !== false) {
      openHubspotAuthDialog(authMessage);
    }
    throw new HubspotAuthRequiredError(authMessage);
  }

  return result;
};

export const initializeHubspotOpportunityFields = async (
  options: HubspotSyncOperationOptions = {}
): Promise<HubspotRequestResult> => {
  const fallbackMessage =
    options.fallbackMessage ?? "HubSpot field initialization failed.";

  try {
    const { response, data, text } = await fetchJsonWithHubspotAuth(
      `${apiBaseUrl}/hubspot-sync/opportunities/v2/auto-init`,
      {
        method: "POST",
      },
      {
        authRequiredMessage:
          options.authRequiredMessage ?? defaultHubspotOpportunityInitAuthMessage,
        openDialogOnAuthError: options.openDialogOnAuthError,
      }
    );
    const result = toObjectPayload(data);

    if (!response.ok || result.success === false) {
      return {
        success: false,
        authRequired: false,
        message: extractApiMessage(result, text, fallbackMessage),
        data: result,
      };
    }

    return {
      success: true,
      message: "",
      data: result,
    };
  } catch (error: unknown) {
    return {
      success: false,
      authRequired: isHubspotAuthRequiredError(error),
      message: error instanceof Error ? error.message : fallbackMessage,
      data: {},
    };
  }
};

export const syncOpportunityToHubspotById = async (
  opportunityId: number,
  options: HubspotSyncOperationOptions = {}
): Promise<HubspotOpportunitySyncResult> => {
  const fallbackMessage =
    options.fallbackMessage ?? `Failed to sync opportunity ${opportunityId} to HubSpot.`;

  try {
    const { response, data, text } = await fetchJsonWithHubspotAuth(
      `${apiBaseUrl}/hubspot-sync/opportunity/${opportunityId}`,
      {
        method: "POST",
      },
      {
        authRequiredMessage: options.authRequiredMessage ?? defaultHubspotSyncAuthMessage,
        openDialogOnAuthError: options.openDialogOnAuthError,
      }
    );
    const result = toObjectPayload(data);

    if (!response.ok) {
      return {
        ...result,
        success: false,
        opportunityId,
        message: extractApiMessage(result, text, fallbackMessage),
      };
    }

    return {
      ...result,
      success: result.success ?? true,
      opportunityId: result.opportunityId ?? opportunityId,
    };
  } catch (error: unknown) {
    return {
      success: false,
      opportunityId,
      authRequired: isHubspotAuthRequiredError(error),
      message: error instanceof Error ? error.message : fallbackMessage,
    };
  }
};
